// The Company's item master, searched by Nexus — the ONE answer to "what stock
// of ours can we point at?" that every picker in the configurator reads.
//
//   POST /api/erp/companies/items/search  →  { items: PortalItem[] }
//
// Two questions, one feed, on purpose. "Which loose part goes in this kit?" and
// "which kit or SKU are we reworking?" are the same Material row on the Nexus
// side, read with the same company reach; `is_kit` and `components` are what
// separate the two readings. Never split them: one question about a Company's
// stock is one query, and a narrower second feed — one admitting only materials
// that already have kit components, say — makes a partner's plain stocked SKU
// pickable in one place and invisible in the other.
//
// SEARCH, not a full catalog dump: an item master runs to thousands of rows, so
// Nexus takes a `query` and clamps the row count (1–100, default 25) and orders
// by name at the data layer. The portal passes the typed text straight through
// and never builds a filter of its own (R25 — the sort and the match both stay
// at the data layer). Nexus matches name, SKU and description, tokenized; the
// picker narrows the page it holds by the SAME rule (util/search-tokens.ts) so
// the two can never disagree about what matched.
//
// `item_ids` is the other read this feed serves: an EXACT-key lookup, complete
// and unbounded by `query`/`limit`, which is what the save boundary's ownership
// check needs (a check bounded by a picker page would refuse a legitimate pick
// that sorted past the limit).
//
// Inactive rows come back on BOTH reads. Inactive is ArgoWMS's flag on the
// partner's own stock record, not a portal decision: the item is still the
// Company's own, so a picker offers it labelled "Inactive" and the ownership
// check accepts it.
//
// `item_id` IS the selection key and is `sku || id` on the Nexus side, so it is a
// WMS SKU for a real stocked item and a Material uuid for one with no SKU yet.
// The portal never parses it; it is echoed back on the kit contents row and as
// the assembly's `bom_id`. A row with no `item_id` (or no `name`) cannot be
// rendered or picked and is dropped, the same way normalizeFulfilledSku drops a
// row with no sku (types/fulfillment-status.ts).
//
// Sanitize-at-boundary, same posture as normalizeFulfillmentStatus:
// every row is narrowed to the allowlist below, so anything else Nexus might
// return — least of all a cost column (R8) — cannot propagate into the portal.
// Nexus's `updated_at` is not ingested: no picker surface renders or sorts by it,
// and adding it would put an unread field in front of the customer.
import { normalizeBomComponent, } from '../vocabulary.js';
// Whether a picker's request asked Nexus to pull the Company's new items from
// ArgoWMS before searching (the `refresh_from_warehouse` query param on the
// portal routes). Only the literal 'true' counts, so a stray or repeated param
// can never turn an ordinary picker read into a warehouse pull.
export function isWarehouseRefreshRequested(raw) {
    return raw === 'true';
}
// Whether a row is the partner's OWN stock rather than Argo's. Every company item
// read carries both; the questions that are only about the partner's own stock —
// "which of YOUR kits are we reworking?", "do you have stock on file at all?" —
// ask this of each row rather than of the read.
export function isPartnerStock(item) {
    return !item.is_argo_stock;
}
// Row count the portal asks for — Nexus's clamp ceiling, sent explicitly so the
// request is not shaped by a far-side default that can move (Nexus clamps 1–100
// and would otherwise default to 25).
//
// The ceiling and not a page, because the picker narrows CLIENT-side too: it
// filters the page it already holds, so anything not fetched is unreachable
// between fetches no matter what the customer types. Asking for 25 would
// silently cap a partner at their first 25 products by name. The picker DOES
// wire its search box through to this endpoint's own `query` parameter
// (CompanyItemPicker), so a company with more than 100 items can still reach
// item 500 by typing its name — the ceiling bounds one page, not the catalog.
export const COMPANY_ITEM_SEARCH_LIMIT = 100;
// Longest search string the portal forwards. A typed picker query is a few words
// at most; anything past this is truncated rather than rejected, so a paste into
// the search box still returns something usable.
export const COMPANY_ITEM_QUERY_MAX_LENGTH = 120;
// Most keys one exact-key read can carry. A quote claims each assembly's kit plus
// one per stocked contents row, so larger claims are read in batches of this
// size; Nexus caps at the same number.
export const COMPANY_ITEM_IDS_MAX = 200;
// Narrow an untrusted search string (an HTTP query param) to what gets
// forwarded. Returns undefined for anything blank, which Nexus reads as "no text
// filter" and answers with the first page of the Company's items.
export function normalizeCompanyItemQuery(raw) {
    if (typeof raw !== 'string')
        return undefined;
    const trimmed = raw.trim().slice(0, COMPANY_ITEM_QUERY_MAX_LENGTH).trim();
    return trimmed.length > 0 ? trimmed : undefined;
}
function optionalString(value) {
    return typeof value === 'string' && value.length > 0 ? value : undefined;
}
// Defensive normalizer for one untrusted item row (Nexus → portal). Returns null
// (dropped) when `item_id` or `name` is absent/blank — an item with no stable key
// cannot be selected, and one with no name cannot be shown.
export function normalizeCompanyItem(raw) {
    if (typeof raw !== 'object' || raw === null)
        return null;
    const r = raw;
    const itemId = optionalString(r.item_id);
    const name = optionalString(r.name);
    if (itemId === undefined || name === undefined)
        return null;
    const components = Array.isArray(r.components)
        ? r.components.map(normalizeBomComponent).filter((c) => c !== null)
        : [];
    const out = {
        item_id: itemId,
        name,
        // The COMPONENTS decide it, not the far-side flag: `is_kit` and the tree
        // arrive from the same row, and a kit whose components were all filtered out
        // by visibility has nothing for the rework pre-fill to read. One fact, one
        // source (R32 — never carry two answers to the same question).
        is_kit: components.length > 0,
        component_count: components.length,
        components,
        status: r.status === 'inactive' ? 'inactive' : 'active',
        // Defaults FALSE on anything but an explicit true, so a row Nexus does not
        // mark reads as the partner's own stock.
        is_argo_stock: r.is_argo_stock === true,
    };
    const sku = optionalString(r.sku);
    if (sku !== undefined)
        out.sku = sku;
    const uom = optionalString(r.uom);
    if (uom !== undefined)
        out.uom = uom;
    const description = optionalString(r.description);
    if (description !== undefined)
        out.description = description;
    return out;
}
// Narrow an untrusted list of selection keys (an HTTP query param, repeated or
// comma-joined) to what gets forwarded on an exact-key read. Deduped and capped;
// an empty result means "no key read", which the caller must treat as "nothing
// claimed" and NOT as "nothing owned".
export function normalizeCompanyItemIds(raw) {
    const parts = [];
    const push = (v) => {
        if (typeof v !== 'string')
            return;
        for (const piece of v.split(',')) {
            const trimmed = piece.trim();
            if (trimmed)
                parts.push(trimmed);
        }
    };
    if (Array.isArray(raw))
        raw.forEach(push);
    else
        push(raw);
    return [...new Set(parts)].slice(0, COMPANY_ITEM_IDS_MAX);
}
// How one item reads in a picker: its SKU first, then its name. The SKU is what
// the partner's own people say out loud ("WIDGET1"), so leading with it is what
// makes the list scannable. An item with no WMS SKU has only its name to show —
// `item_id` there is a Nexus Material uuid, which would read as noise.
//
// ONE derivation, used by every picker that offers these rows, so the same item
// reads identically wherever it is offered and however it was frozen onto a saved
// row.
export function companyItemLabel(item) {
    return item.sku ? `${item.sku} - ${item.name}` : item.name;
}
// Everything a picker matches an item by, beyond its visible label: the fields
// Nexus itself matched server-side (name, SKU, description) plus the unit, which
// is on screen in the row beside the picker. Kept in one place so the client
// narrowing cannot drift from the server's — see util/search-tokens.ts.
export function companyItemSearchTerms(item) {
    return [item.sku, item.name, item.description, item.uom].filter((v) => typeof v === 'string' && v.length > 0);
}
// Project a picked item into the display snapshot stored on the assembly config
// (`bom_snapshot`). Keeps only the display fields — the selection key itself is
// stored alongside as `bom_id`, and `status`/`uom` are picker-only metadata.
//
// Display-continuity, NOT a cache: the snapshot is what keeps the recap reading
// correctly after the catalog changes under it, which is why the name is frozen
// at pick time rather than re-resolved on read (R26 — never re-derive a stored
// intent from a moving input).
export function bomSnapshotFromCompanyItem(item) {
    const out = {
        name: item.name,
        component_count: item.component_count,
        components: item.components,
    };
    // The kit's own SKU, under the name the snapshot has always used for it.
    if (item.sku)
        out.bom_number = item.sku;
    return out;
}
//# sourceMappingURL=company-items.js.map